import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmModal';
import { fetchCsrfToken } from './utils/api';

// Pages
import IntakeForm from './pages/IntakeForm';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SubmissionDetail from './pages/SubmissionDetail';
import StatusCheck from './pages/StatusCheck';
import UsersManagement from './pages/UsersManagement';
import Analytics from './pages/Analytics';
import EmailTemplates from './pages/EmailTemplates';
import SubmissionConfigs from './pages/SubmissionConfigs';
import NotFound from './pages/NotFound';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  // Fetch CSRF token on app initialization
  useEffect(() => {
    fetchCsrfToken();
  }, []);

  return (
    <ToastProvider>
      <ConfirmProvider>
        <div className="min-h-screen bg-gray-50">
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={<IntakeForm submissionType="text_giving" />} />
        <Route path="/intake" element={<IntakeForm submissionType="text_giving" />} />
        <Route path="/intake/messaging" element={<IntakeForm submissionType="messaging" />} />
        <Route path="/status" element={<StatusCheck />} />
        <Route path="/login" element={<Login />} />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/submissions/:id"
          element={
            <ProtectedRoute>
              <SubmissionDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UsersManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/email-templates"
          element={
            <ProtectedRoute>
              <EmailTemplates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/submission-configs"
          element={
            <ProtectedRoute>
              <SubmissionConfigs />
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </div>
      </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
